/**
 * space_sun — the sector's sun as a STAR (report §9 R3).
 *
 * Three camera-facing quads riding the ship at a fixed far distance along the sun direction (D, inside the
 * 3,600 km far plane and behind every anchored body, so a planet in front hides the disc by depth):
 *  1. DISC — HDR emission (SUN_DISC_EMISSION, far above bloom 1.0 and flares 4) under the quadratic limb-
 *     darkening law, two scales of animated Worley granulation (±6 %), slow darker sunspots and a thin warm
 *     chromosphere rim. Depth-tested.
 *  2. CORONA — (r/R)^−2.5 with fBm streamers in the polar angle. Depth-tested: a planet across the sun hides
 *     the disc and the corona behind it, and the rest stays — an eclipse reads as one.
 *  3. GLARE — the anamorphic streak, a starburst and a soft halo: a LENS effect, drawn over everything and
 *     faded by the analytic ECLIPSE fraction (space_look.eclipseVisible — the caller's CPU disc overlap
 *     with every planet / capital sphere), so a sun behind a planet flares nothing.
 * The quads' size follows the sun's ANGULAR radius (sys0's fixed game sun; an alt system's star by
 * asin(R/d) from the ship), capped so a close star never grows the corona past ~70°.
 *
 * One material per quad, built once per session (`userData.shared`); only uniforms move per frame.
 *
 * STAR KINDS (natural objects, 2026-10-07 — lib/space/natural/star_look.ts says what each kind is). The disc shader's
 * constants are UNIFORMS now, a SURFACE: the limb law and how red the limb is, the granules' size, contrast and pace, the
 * spots' cover and banding, the rim, the edge's softness, a turning sphere under the noise (spots cross the disc), an
 * oblate outline with brighter poles (a Be star), a tear-drop nose (a donor filling its Roche lobe), two bright patches
 * (a flare, an accretion spot) and an OCCLUDER disc (the other star of a pair — additive quads cannot hide each other,
 * so each star's shader is told where the nearer one stands and draws nothing there: an eclipse is real).
 * THE DEFAULT SURFACE IS THE SUN'S, TO THE BIT: every new term is written so that its default is an exact identity
 * (×1, +0, ÷1, a far-away occluder), the old constants are the uniforms' defaults in the old order of operations — the
 * Commons Reach's sun, which never sets a surface, is the picture it was. `resetSunSurface()` puts the rig back.
 * A PAIR's second star is another BODY (`starBody(i)`): the same three quads on their own uniforms.
 *
 * THE STAR BEHIND A BLACK HOLE (space look, 2026-10-08 — "a black area around the black hole instead of bending the
 * light behind it"). The quads are geometry: the lens (enginex/render/space/blackhole) bends the SKY, and its opaque body
 * cap simply hid them. A star is now also a LENS SOURCE (`starLensSource`): the SAME disc and corona functions the quads
 * draw (`discParts`, `coronaRgb` — one surface, two parametrisations), evaluated where the pixel's ESCAPING ray passes
 * the star. While a lens is shown it OWNS the star's disc and corona (`setBodyLensOwned`: the two quads are hidden, so
 * nothing is drawn twice and there is no seam at the cap's edge — the lens draws the star in its sky pass and in its
 * cap with one function); the glare — a camera effect — stays a quad, seated on the star's IMAGE.
 */
import * as THREE from 'three/webgpu';
import {
  Fn, If, uniform, vec2, vec3, vec4, float, uv, length, atan, sqrt, max, min, exp, pow, abs, cos, dot, smoothstep, time, fwidth, clamp,
  mix, step, dFdx, dFdy, mx_worley_noise_float, mx_noise_float, mx_fractal_noise_float,
} from 'three/tsl';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import { SUN_DISC_EMISSION, coronaExtentRadii, spaceEyeState, wideQuadWeight, wideQuadQuat, wideQuadDistance, WIDE_EDGE_FROM, wideEdgeCapShare } from './space_look.js';
import { LENS_POINT_SIGMA, LENS_POINT_FROM, LENS_POINT_FULL } from './black_hole.js';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type N = any;

/** Render distance of the sun quads (m) — inside the far plane, behind every anchor (≤100 km). */
export const SUN_QUAD_D = 2_500_000;
export const DISC_EXT = 1.15;     // the disc quad's half-extent in disc radii (room for the rim)
const CORONA_EXT = 9;      // the corona quad's half-extent in disc radii (before the angle cap)
const GLARE_EXT = 42;      // the glare quad's half-extent in disc radii (before the angle cap)
const MAX_HALF_ANGLE = 1.2; // rad — no quad reaches past ~70° off the sun's centre
const MAX_DISC_ANGLE = 1.0; // rad — the disc itself (a star this wide fills the sky: the ship is at its surface)

/** A star's surface as the disc and corona shaders draw it. Every default is the Sun's (the Commons Reach's picture). */
export interface SunSurface {
  /** Limb darkening I(μ) = a + bμ − cμ². */
  limb: [number, number, number];
  /** How much of green and blue the limb loses (the limb is cooler gas): colour × (1 − limbRed·(1 − μ)). */
  limbRed: [number, number, number];
  /** The edge's softness (disc radii; never sharper than a pixel). */
  fuzz: number;
  /** Granulation: two cell scales, their contrasts, their boil rates. */
  gran: [number, number, number, number];
  granSpeed: [number, number];
  /** Spots: the field's dark-from / fully-dark thresholds, the depth, the drift rate, the pattern's scale per axis (a stretched x and z = bands). */
  spot: [number, number, number, number];
  spotScale: [number, number, number];
  /** True = the noise lives on a turning SPHERE (spots cross the disc and foreshorten at the limb); false = the Sun's flat field. */
  sphere: boolean;
  /** The chromosphere's rim: colour, gain, width (disc radii). */
  rim: [number, number, number, number];
  rimWidth: number;
  /** The corona's radial power, and how strongly it breaks into streamers (0 = a smooth halo: a wind, not a magnetic corona). */
  coronaPow: number;
  coronaRays: number;
  /** A bright patch's colour (linear) and angular width on the sphere (chord, sphere radii). */
  patchRgb: [number, number, number];
  patchWidth: number;
}
export const SUN_SURFACE: Readonly<SunSurface> = {
  limb: [0.30, 0.94, 0.24], limbRed: [0, 0, 0], fuzz: 0.004,
  gran: [13, 37, 0.07, 0.05], granSpeed: [0.035, 0.08],
  spot: [0.52, 0.62, 0.55, 0.004], spotScale: [2.3, 2.3, 2.3], sphere: false,
  rim: [1.0, 0.42, 0.22, 0.12], rimWidth: 0.03, coronaPow: -2.5, coronaRays: 0.9,
  patchRgb: [1, 1, 1], patchWidth: 0.3,
};

interface StarU {
  color: N; emission: N; coronaExt: N; glareExt: N; glare: N; corona: N;
  /** THE WIDE STAR'S EDGE: `wideFade` (the glare's) / `coronaFade` (the corona's: × the cap's share) 0 = the old edge fade (the Commons sun), 1 = the fade by angle; each `*Half` = (tan of the quad's half-angle, the half-angle in rad). */
  wideFade: N; coronaFade: N; coronaHalf: N; glareHalf: N;
  limb: N; limbRed: N; fuzz: N; gran: N; granSpeed: N; fore: N; sphereZ: N; spin0: N; spin1: N; spin2: N;
  spot: N; spotScale: N; rim: N; rimWidth: N; coronaPow: N; coronaRays: N; patchA: N; patchB: N; patchLook: N;
  shape: N; nose: N; occ: N; occK: N;
}
function starUniforms(): StarU {
  const s = SUN_SURFACE;
  return {
    color: uniform(new THREE.Color(1, 0.95, 0.88)), emission: uniform(SUN_DISC_EMISSION),
    coronaExt: uniform(CORONA_EXT), glareExt: uniform(GLARE_EXT), glare: uniform(1), corona: uniform(1),
    wideFade: uniform(0), coronaFade: uniform(0), coronaHalf: uniform(new THREE.Vector2(1, 0.7854)), glareHalf: uniform(new THREE.Vector2(1, 0.7854)),
    limb: uniform(new THREE.Vector3(...s.limb)), limbRed: uniform(new THREE.Vector3(...s.limbRed)), fuzz: uniform(s.fuzz),
    gran: uniform(new THREE.Vector4(...s.gran)), granSpeed: uniform(new THREE.Vector2(...s.granSpeed)),
    fore: uniform(new THREE.Vector2(0.35, 0.65)),       // the flat field's foreshortening a + bμ (a sphere needs none: 1, 0)
    sphereZ: uniform(0),                                // × the sphere's z in the noise (0 = the flat field)
    spin0: uniform(new THREE.Vector3(1, 0, 0)), spin1: uniform(new THREE.Vector3(0, 1, 0)), spin2: uniform(new THREE.Vector3(0, 0, 1)),
    spot: uniform(new THREE.Vector4(...s.spot)), spotScale: uniform(new THREE.Vector3(...s.spotScale)),
    rim: uniform(new THREE.Vector4(...s.rim)), rimWidth: uniform(s.rimWidth), coronaPow: uniform(s.coronaPow), coronaRays: uniform(s.coronaRays),
    patchA: uniform(new THREE.Vector4(0, 0, 1, 0)), patchB: uniform(new THREE.Vector4(0, 0, 1, 0)),
    patchLook: uniform(new THREE.Vector4(...s.patchRgb, s.patchWidth)),
    shape: uniform(new THREE.Vector4(0, 1, 1, 0)),      // the spin axis on screen (unit), 1 ÷ the apparent polar radius, gravity darkening
    nose: uniform(new THREE.Vector4(1, 0, 0, 2)),       // the tear-drop: its direction on screen (unit), how far it reaches, how pointed
    occ: uniform(new THREE.Vector4(1e4, 1e4, 0, 0.01)), // the occluder: centre (disc radii, the quad's frame), radius, edge softness
    occK: uniform(1),                                   // how much of the overlap it takes (a pair at one depth cross-fades)
  };
}
const val = <T>(u: N): T => (u as { value: T }).value;

export const sunState = { built: false, angR: 0, visible: 1, emission: SUN_DISC_EMISSION, color: [1, 0.95, 0.88] as number[], dir: [0, 1, 0] as number[], coronaExt: CORONA_EXT };

function quad(name: string, colorNode: N, depthTest: boolean, renderOrder: number): THREE.Mesh {
  const mat = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, depthTest, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
  mat.colorNode = colorNode;
  mat.fog = false;
  mat.toneMapped = true;
  markMrtOverlay(mat);
  mat.userData.shared = true;
  mat.name = name;
  const geo = new THREE.PlaneGeometry(2, 2);
  geo.userData.shared = true;
  const m = new THREE.Mesh(geo, mat);
  m.name = name;
  m.frustumCulled = false;
  m.renderOrder = renderOrder;
  return m;
}

/**
 * A body as the black-hole lens reads it (uniforms, written each frame by `writeBodyLens`): `sHat` the unit direction to
 * its centre — from the HOLE when it stands beyond it, from the camera when it stands nearer (`front` 1); `kappa` r_s ÷
 * its distance from the hole (0 = a sky direction); `rho` its radius ÷ that distance (the sine of its angular radius
 * from there); `right` / `up` the quad's frame (the camera's); `on` 0 = draw nothing.
 */
export interface StarLensU { sHat: N; kappa: N; rho: N; right: N; up: N; front: N; on: N }
/** One star as the sector draws it: its three quads and the uniforms they read. */
export interface StarBody {
  group: THREE.Group; disc: THREE.Mesh; corona: THREE.Mesh; glare: THREE.Mesh; u: StarU; angR: number; glareScale: number;
  /** The lens source's uniforms. */
  lens: StarLensU;
  /** True while a black-hole lens draws this star's disc and corona (the two quads are hidden). */
  lensOwned: boolean;
  /** Where it was last placed: the unit direction from the camera, its distance (Infinity = a sky direction, the Commons Reach's sun). */
  dirNow: THREE.Vector3; distNow: number;
  /**
   * The quads' own frame as last placed (THE WIDE-QUAD LAW): `frameRight` / `frameUp` span the quad, `frameN` is its
   * normal (toward the eye). The camera's right / up / +Z for a small star; turned onto the star's direction for a wide
   * one. Whoever seats something ON a quad (a pair's occluder, a star's outline) measures in this frame.
   */
  frameRight: THREE.Vector3; frameUp: THREE.Vector3; frameN: THREE.Vector3;
  /** How far from the eye its quads stand this frame: `SUN_QUAD_D`, nearer for a wide star (THE FAR-PLANE ROOM). */
  quadD: number;
}

/** `localStorage atlas:sun-glow-edge = 'hard'` keeps a wide star's corona and glare on the old edge fade (THE WIDE STAR'S EDGE) — the owner's A/B. */
let glowEdge: boolean = ((): boolean => { try { return !(typeof localStorage !== 'undefined' && localStorage.getItem('atlas:sun-glow-edge') === 'hard'); } catch { return true; } })();
/** A probe's live switch for the same thing. */
export function setStarGlowEdge(on: boolean): void { glowEdge = !!on; }
/** `localStorage atlas:sun-quads = 'flat'` keeps every star's quads parallel to the image plane (the seat before THE WIDE-QUAD LAW) — the owner's A/B. */
let quadMode: 'auto' | 'flat' = ((): 'auto' | 'flat' => { try { return typeof localStorage !== 'undefined' && localStorage.getItem('atlas:sun-quads') === 'flat' ? 'flat' : 'auto'; } catch { return 'auto'; } })();
/** A probe's live switch for the same thing (the next frame's placement obeys it). */
export function setStarQuadMode(m: 'auto' | 'flat'): void { quadMode = m; }
export function starQuadMode(): 'auto' | 'flat' { return quadMode; }
const _wq: number[] = [0, 0, 0, 1];

// p ∈ [−1, 1]² across a quad.
const P = (): N => uv().sub(0.5).mul(2);
/** What the other star of a pair leaves of this one at a point `pd` (disc radii, the quad's frame): 1 outside its disc. */
const occluded = (u: StarU, pd: N): N => {
  const o = smoothstep(u.occ.z.sub(u.occ.w), u.occ.z.add(u.occ.w), length(pd.sub(vec2(u.occ.x, u.occ.y))));
  return float(1).sub(u.occK.mul(float(1).sub(o)));
};

/**
 * The disc at `p` — the quad's coordinate, [−1, 1]² (a disc radius is 1 / DISC_EXT of it): limb, granules, spots, patches,
 * rim. `aaIn` = the edge's softness in disc radii when the caller knows its own footprint (the lens — no derivative is
 * taken here then, so the call may stand inside a branch); the quads take the pixel's own `fwidth`.
 * THE DEFAULT SURFACE IS THE SUN'S, TO THE BIT: the quads' graph is this function's, node for node, as before.
 */
function discParts(u: StarU, p: N, aaIn?: N): { rgb: N; inside: N } {
    // the outline: an ellipse about the spin axis (a fast rotator is oblate), then a nose toward the companion
    const pe = p.x.mul(u.shape.y).sub(p.y.mul(u.shape.x));
    const pa = p.x.mul(u.shape.x).add(p.y.mul(u.shape.y)).mul(u.shape.z);
    const pS = vec2(pe, pa);
    const toward = dot(pS, vec2(u.nose.x, u.nose.y)).div(max(length(pS), float(1e-5)));
    const r = length(pS).mul(DISC_EXT).div(float(1).add(u.nose.z.mul(pow(max(toward, float(0)), u.nose.w)))); // in disc radii
    const mu = sqrt(max(float(0), float(1).sub(r.mul(r))));
    const limb = u.limb.x.add(mu.mul(u.limb.y)).sub(mu.mul(mu).mul(u.limb.z));
    // the point of the surface under this pixel, turned into the star's own frame (the flat field: z = 0, no turn)
    const n = vec3(pS.x, pS.y, mu.mul(u.sphereZ).div(DISC_EXT));
    const nr = vec3(dot(u.spin0, n), dot(u.spin1, n), dot(u.spin2, n));
    // Granulation: two drifting Worley scales, foreshortened toward the limb (cells flatten at grazing μ).
    const q = vec2(nr.x, nr.y).div(max(float(0.35), mu.mul(u.fore.y).add(u.fore.x)));
    const g1 = mx_worley_noise_float(vec3(q.mul(u.gran.x), nr.z.mul(u.gran.x).add(time.mul(u.granSpeed.x))));
    const g2 = mx_worley_noise_float(vec3(q.mul(u.gran.y).add(3.1), nr.z.mul(u.gran.y).add(time.mul(u.granSpeed.y)).add(7.3)));
    const gran = float(1).add(g1.sub(0.45).mul(u.gran.z)).add(g2.sub(0.45).mul(u.gran.w));
    // Sunspots: a slow low-frequency field, thresholded — umbra-dark cores, rare.
    const s = mx_noise_float(vec3(nr.x.mul(u.spotScale.x), nr.y.mul(u.spotScale.y), nr.z.mul(u.spotScale.z).add(time.mul(u.spot.w))));
    const spots = float(1).sub(smoothstep(u.spot.x, u.spot.y, s).mul(u.spot.z));
    // von Zeipel: a fast rotator's poles are hotter than its bulging equator
    const sinLat = nr.y.mul(DISC_EXT);
    const poles = float(1).add(u.shape.w.mul(sinLat.mul(sinLat).sub(0.4)));
    const aa = aaIn ?? max(fwidth(r), u.fuzz);
    const inside = float(1).sub(smoothstep(float(1).sub(aa), float(1).add(aa), r));
    const vis = occluded(u, p.mul(DISC_EXT));
    const disc = limb.mul(gran).mul(spots).mul(poles).mul(inside).mul(u.emission).mul(vis);
    // bright patches riding the surface: a flare, the foot of an accretion column
    const nu = nr.mul(DISC_EXT);
    const dA = nu.sub(vec3(u.patchA.x, u.patchA.y, u.patchA.z)), dB = nu.sub(vec3(u.patchB.x, u.patchB.y, u.patchB.z));
    const w2 = u.patchLook.w.mul(u.patchLook.w);
    const patch = exp(dot(dA, dA).div(w2).negate()).mul(u.patchA.w).add(exp(dot(dB, dB).div(w2).negate()).mul(u.patchB.w))
      .mul(inside).mul(u.emission).mul(vis);
    // Chromosphere: a thin warm rim at the limb.
    const rimD = r.sub(1.0).div(u.rimWidth);
    const rim = exp(rimD.mul(rimD).negate()).mul(u.emission).mul(u.rim.w).mul(vis);
    const tint = vec3(1, 1, 1).sub(vec3(u.limbRed).mul(float(1).sub(mu)));
    return { rgb: vec3(u.color).mul(tint).mul(disc).add(vec3(u.patchLook.x, u.patchLook.y, u.patchLook.z).mul(patch)).add(vec3(u.rim.x, u.rim.y, u.rim.z).mul(rim)), inside };
}

/**
 * THE WIDE STAR'S EDGE (space_look.wideEdgeProfile is the CPU mirror): the fade of a quad over the last three tenths
 * of its ANGLE. `frac` = the distance from the quad's centre ÷ its half-size (tangent units), `half` = (tan of its
 * half-angle, the half-angle): the true angle off the star is atan(frac · tan half). 1 − smoothstep (not squared:
 * the display is AgX — space_look.ts says what the square cost).
 */
function wideEdge(frac: N, half: N): N {
  const t = clamp(atan(frac.mul(half.x)).div(half.y).sub(WIDE_EDGE_FROM).div(1 - WIDE_EDGE_FROM), 0, 1);
  return float(1).sub(t.mul(t).mul(float(3).sub(t.mul(2))));
}
/** The corona at `p` — its quad's coordinate, [−1, 1]² (the quad reaches `coronaExt` disc radii): (r/R)^pow with streamers, nothing inside the disc. */
function coronaRgb(u: StarU, p: N): N {
    const r = length(p).mul(u.coronaExt);
    const th = atan(p.y, p.x);
    const streamer = mx_fractal_noise_float(vec3(cos(th).mul(2.4), cos(th.sub(1.5708)).mul(2.4), time.mul(0.008)), 3, 2.0, 0.5, 1.0);
    const radial = clamp(streamer.mul(u.coronaRays).add(0.55), 0.15, 1.8);
    const fall = pow(max(r, float(1)), u.coronaPow);
    const edge = mix(float(1).sub(smoothstep(u.coronaExt.mul(0.6), u.coronaExt, r)), wideEdge(length(p), u.coronaHalf), u.coronaFade);
    const inner = smoothstep(0.96, 1.04, r); // the disc owns r < 1
    return vec3(u.color).mul(fall.mul(radial).mul(edge).mul(inner).mul(u.corona).mul(2.4).mul(occluded(u, p.mul(u.coronaExt))));
}

/**
 * THE STAR AS THE BLACK-HOLE LENS DRAWS IT. `ray` is the lens's (enginex BlackHoleRay): the un-bent view ray, the escape
 * direction and a point of the escape asymptote (hole-relative, r_s). The star's centre is at `sHat` (÷ `kappa` r_s away;
 * 0 = at infinity): the ray's perpendicular miss, in units of the star's own distance, over `rho` is the point of the
 * disc the pixel looks at — in DISC RADIUS units, in the quad's right / up frame — and the quads' own functions shade it.
 * That is exact for a sphere at any distance (the silhouette is |miss| = R), and with no hole to speak of the escaping
 * line IS the view ray, so the lens draws the star where the quads did.
 *
 * The footprint: lensing conserves surface brightness, so a resolved image needs no gain — but the mirrored image far
 * off-axis and the images wound round the photon ring are thinner than a pixel, and a point sample of a disc 16× the
 * bloom threshold would sparkle. One pixel's extent in disc radii (`foot`, the long axis — safe, as the lens's own gas
 * filter is) softens the limb, and past `LENS_POINT_FROM` the disc becomes a flux-conserving Gaussian (black_hole.ts
 * `lensPointProfile` is its CPU mirror, `limbMean` the mean of the limb law it carries).
 * Both branches hold no derivative and no texture sample (WGSL's uniformity; THE PLACEMENT LAW: everything they share is
 * a var declared above them).
 *
 * Returns `behind` (vec3, light the lens bends — it joins the sky under the shadow and the disc) and `front`
 * (vec4 rgb + opacity, a star nearer than the hole — drawn over both); one of the two is zero, by the `front` uniform.
 */
export function starLensSource(b: StarBody, ray: { view: N; dir: N; offset: N }): { behind: N; front: N } {
  const u = b.u, L = b.lens;
  const front = float(L.front).toVar();
  const rayDir = mix(vec3(ray.dir), vec3(ray.view), front).toVar();
  const w = vec3(L.sHat).sub(vec3(ray.offset).mul(float(L.kappa).mul(float(1).sub(front)))).toVar();
  const t = dot(w, rayDir).toVar();
  const q = w.sub(rayDir.mul(t)).toVar();
  // The pixel's place on the star, in disc radii: the ray passes the centre at −q, so the point it looks at is −q. Its
  // DISTANCE from the centre is |q| ÷ rho exactly (a sphere is round from anywhere); `right` / `up` — the camera's, made
  // perpendicular to the star's direction on the CPU — only give it its bearing. Dotting q with the camera's own right /
  // up loses q's part along the view axis: 28° off it the disc came out 9 % too large (the bed's oracle, 2026-10-08).
  const qf = vec2(dot(q, vec3(L.right)), dot(q, vec3(L.up))).toVar();
  const pd = qf.mul(length(q).div(max(length(qf), float(1e-20)))).negate().div(L.rho).toVar();
  const foot = max(length(dFdx(pd)), length(dFdy(pd))).toVar();
  const r = length(pd).toVar();
  // The limb's softness is the source RADIUS one pixel step crosses (|∇r|, as the quads' fwidth(r)) — not the footprint's
  // long axis: a mirrored arc is squeezed across and stretched along, and `foot` there smeared its tips four pixels out.
  const edge = length(vec2(dFdx(r), dFdy(r))).toVar();
  const live = step(0, t).mul(L.on).toVar();                      // the star is AHEAD along the ray, and this body is drawn
  const rgb = vec3(0).toVar(), op = float(0).toVar();
  If(live.greaterThan(0.5).and(r.lessThan(foot.mul(2.5).add(1.5))), () => {
    const d = discParts(u, pd.div(DISC_EXT), max(edge, u.fuzz));
    const s2 = max(foot.mul(foot).mul(LENS_POINT_SIGMA * LENS_POINT_SIGMA), float(1e-6));
    const point = float(1).sub(exp(float(-0.5).div(s2))).mul(exp(r.mul(r).div(s2.mul(2).add(0.5)).negate()));
    const meanI = u.limb.x.add(u.limb.y.mul(2 / 3)).sub(u.limb.z.mul(0.5));
    const pointRgb = vec3(u.color).mul(vec3(1, 1, 1).sub(vec3(u.limbRed).div(3))).mul(u.emission.mul(meanI).mul(point).mul(occluded(u, pd)));
    const k = smoothstep(LENS_POINT_FROM, LENS_POINT_FULL, foot);
    rgb.assign(mix(d.rgb, pointRgb, k));
    op.assign(mix(d.inside, min(point, float(1)), k));
  });
  If(live.greaterThan(0.5).and(r.lessThan(u.coronaExt)), () => {
    // a pixel that spans more of the corona than its own distance from the star carries no picture of it
    const keep = float(1).sub(smoothstep(0.5, 2.0, foot.div(max(r, float(1)))));
    rgb.addAssign(coronaRgb(u, pd.div(u.coronaExt)).mul(keep));
  });
  return { behind: rgb.mul(float(1).sub(front)), front: vec4(rgb.mul(front), op.mul(front)) };
}

function lensUniforms(): StarLensU {
  return {
    sHat: uniform(new THREE.Vector3(0, 1, 0)), kappa: uniform(0), rho: uniform(0.0096),
    right: uniform(new THREE.Vector3(1, 0, 0)), up: uniform(new THREE.Vector3(0, 1, 0)), front: uniform(0), on: uniform(0),
  };
}

function buildBody(tag: string, u: StarU, order: number): StarBody {
  const discColor = Fn(() => discParts(u, P()).rgb)();
  const coronaColor = Fn(() => coronaRgb(u, P()))();

  const glareColor = Fn(() => {
    const p = P();
    const q = p.mul(u.glareExt);                                        // disc radii, screen-aligned (the quad faces the camera)
    const r = length(q);
    const th = atan(q.y, q.x);
    const streak = exp(q.y.div(0.45).mul(q.y.div(0.45)).negate()).mul(exp(abs(q.x).div(u.glareExt.mul(0.22)).negate())).mul(1.8);
    const burst = pow(abs(cos(th.mul(3))), float(90)).mul(exp(r.div(7).negate())).mul(0.9)
      .add(pow(abs(cos(th.mul(4).add(0.4))), float(120)).mul(exp(r.div(4.5).negate())).mul(0.6));
    const halo = exp(r.div(2.6).negate()).mul(0.5).add(exp(r.div(12).negate()).mul(0.08));
    const edge = mix(float(1).sub(smoothstep(u.glareExt.mul(0.7), u.glareExt, r)), wideEdge(length(p), u.glareHalf), u.wideFade);
    return vec3(u.color).mul(streak.add(burst).add(halo).mul(edge).mul(u.glare));
  })();

  const group = new THREE.Group();
  group.name = tag;
  const corona = quad(`${tag}-corona`, coronaColor, true, order);
  const disc = quad(`${tag}-disc`, discColor, true, order + 1);
  const glare = quad(`${tag}-glare`, glareColor, false, 990);
  group.add(corona, disc, glare);
  return { group, disc, corona, glare, u, angR: 0, glareScale: 1, lens: lensUniforms(), lensOwned: false, dirNow: new THREE.Vector3(0, 1, 0), distNow: Infinity,
    frameRight: new THREE.Vector3(1, 0, 0), frameUp: new THREE.Vector3(0, 1, 0), frameN: new THREE.Vector3(0, 0, 1), quadD: SUN_QUAD_D };
}

let rig: StarBody | null = null;

/** Build (once) the sun's three quads. The caller parents `group` under the sector root each activate. */
export function sunRig(): StarBody {
  if (rig) return rig;
  rig = buildBody('space-sun', starUniforms(), -8);
  sunState.built = true;
  return rig;
}

const bodies: StarBody[] = [];
/** A pair's other star (session-shared like the rig; the caller parents its group under the sector root and takes it off again). */
export function starBody(i: number): StarBody {
  return (bodies[i] ??= buildBody(`space-star-${i}`, starUniforms(), -6 + i * 2));
}

/** One disc radius of a body of angular radius `angR`, in metres on its quad (what an occluder's place and size are measured in). */
export function discUnitM(angR: number): number {
  return SUN_QUAD_D * Math.tan(Math.min(MAX_DISC_ANGLE, Math.max(1e-4, angR)));
}
/**
 * Seat one body's quads: `camLocal` the camera in the root's frame, `camWorldQuat` its orientation, `dir` the unit
 * direction to the star, `angR` its angular radius (rad), `visible` how much of its disc is in view (the glare's gain).
 */
export function placeBody(b: StarBody, camLocal: THREE.Vector3, camWorldQuat: THREE.Quaternion, dir: THREE.Vector3, angR: number, visible: number, distM = Infinity): number {
  // THE WIDE-QUAD LAW (space_look.wideQuadWeight): a small star's quads lie in the image plane at SUN_QUAD_D, as they
  // always did; a wide star's face the eye — their coordinate is then the tangent of the angle off the STAR and the 69°
  // cap is a cap — and stand near enough that their far corners stay inside the far plane (wideQuadDistance).
  const wide = quadMode === 'flat' ? 0 : wideQuadWeight(Math.min(MAX_HALF_ANGLE, Math.max(1e-4, angR) * GLARE_EXT));
  const widest = Math.max(GLARE_EXT, spaceEyeState.exposure > 0 ? coronaExtentRadii(spaceEyeState.exposure, spaceEyeState.base) : CORONA_EXT);
  const D = wideQuadDistance(SUN_QUAD_D, Math.tan(Math.min(MAX_HALF_ANGLE, Math.max(1e-4, angR) * widest)), wide);
  b.quadD = D;
  b.group.position.copy(dir).multiplyScalar(D).add(camLocal);
  // what a black-hole lens needs to draw this star: where it is, and how far (a sky direction when no distance is given)
  b.dirNow.copy(dir); b.distNow = distM > 0 ? distM : Infinity;
  b.glare.position.set(0, 0, 0);                                 // the lens re-seats the glare on the star's IMAGE after this, each frame
  const size = (halfAngle: number): number => D * Math.tan(Math.min(MAX_HALF_ANGLE, halfAngle));
  const a = Math.max(1e-4, angR);
  if (wide > 0) {
    wideQuadQuat(camWorldQuat.x, camWorldQuat.y, camWorldQuat.z, camWorldQuat.w, dir.x, dir.y, dir.z, wide, _wq);
    b.disc.quaternion.set(_wq[0], _wq[1], _wq[2], _wq[3]);
  } else b.disc.quaternion.copy(camWorldQuat);
  b.corona.quaternion.copy(b.disc.quaternion); b.glare.quaternion.copy(b.disc.quaternion);
  b.frameRight.set(1, 0, 0).applyQuaternion(b.disc.quaternion); b.frameUp.set(0, 1, 0).applyQuaternion(b.disc.quaternion); b.frameN.set(0, 0, 1).applyQuaternion(b.disc.quaternion);
  // THE TANGENT LAW (2026-10-07): a flat quad's coordinate is linear in the TANGENT of the angle off its centre, never in
  // the angle. The shaders measure r in disc radii across the quad, so each quad is sized — and its extent handed over —
  // in units of the disc's tangent radius D·tan(a). Handing the corona `coronaHalf / a` (a ratio of angles) put its inner
  // edge at 1.23 disc radii for a 9.6° disc and at 2 by the 1.2 rad cap: a dark ring round every star seen from close by
  // (the 0.55° sun of the Commons Reach hid it: tan x ≈ x).
  const tanA = Math.tan(Math.min(MAX_DISC_ANGLE, a));
  const sDisc = D * tanA * DISC_EXT;
  b.disc.scale.set(sDisc, sDisc, 1);
  // THE CORONA FOLLOWS THE EYE (2026-09-27, space_look.coronaExtentRadii): its reach is where its radiance meets the
  // display floor at the sector eye's exposure — a dark-adapted eye sees a long corona, one stopped down a short one.
  const coronaExt = spaceEyeState.exposure > 0 ? coronaExtentRadii(spaceEyeState.exposure, spaceEyeState.base) : CORONA_EXT;
  const coronaHalf = Math.min(MAX_HALF_ANGLE, a * coronaExt);
  b.corona.scale.setScalar(size(coronaHalf)); b.corona.scale.z = 1;
  b.u.coronaExt.value = Math.tan(coronaHalf) / tanA;
  const glareHalf = Math.min(MAX_HALF_ANGLE, a * GLARE_EXT);
  b.glare.scale.setScalar(size(glareHalf)); b.glare.scale.z = 1;
  b.u.glareExt.value = Math.tan(glareHalf) / tanA;
  // THE WIDE STAR'S EDGE: a wide star's glare fades over the last three tenths of its angle (weight 0 = the old edge); its
  // corona does too when the cap cut it short — one that ends where its own light does keeps the edge it had
  b.u.wideFade.value = glowEdge ? wide : 0;
  b.u.coronaFade.value = glowEdge ? wide * wideEdgeCapShare(coronaHalf, MAX_HALF_ANGLE) : 0;
  val<THREE.Vector2>(b.u.coronaHalf).set(Math.tan(coronaHalf), coronaHalf); val<THREE.Vector2>(b.u.glareHalf).set(Math.tan(glareHalf), glareHalf);
  b.u.glare.value = Math.max(0, Math.min(1, visible)) * b.glareScale;
  b.angR = a;
  return coronaExt;
}

/**
 * Seat the quads for this frame: `camLocal` the camera in the root's frame, `camWorldQuat` its orientation,
 * `dir` the unit direction to the sun, `angR` its angular radius (rad), `visible` the eclipse fraction.
 */
export function placeSun(camLocal: THREE.Vector3, camWorldQuat: THREE.Quaternion, dir: THREE.Vector3, angR: number, visible: number, distM = Infinity): void {
  if (!rig) return;
  const coronaExt = placeBody(rig, camLocal, camWorldQuat, dir, angR, visible, distM);
  sunState.angR = rig.angR; sunState.visible = visible; sunState.dir = [dir.x, dir.y, dir.z]; sunState.coronaExt = coronaExt;
}

/** A body's colour (linear rgb, max channel 1), disc emission and corona gain. */
export function setBodyLook(b: StarBody, rgb: readonly [number, number, number], emission = SUN_DISC_EMISSION, corona = 1): void {
  val<THREE.Color>(b.u.color).setRGB(rgb[0], rgb[1], rgb[2]);
  b.u.emission.value = emission;
  b.u.corona.value = corona;
}
/** The star's colour (linear rgb, max channel 1) and the disc emission. */
export function setSunLook(rgb: [number, number, number], emission = SUN_DISC_EMISSION, corona = 1): void {
  setBodyLook(sunRig(), rgb, emission, corona);
  sunState.emission = emission; sunState.color = [...rgb];
}

/** Give a body a kind's surface (space_sun.SunSurface; `SUN_SURFACE` is the Sun's). */
export function setBodySurface(b: StarBody, s: Readonly<SunSurface>): void {
  const u = b.u;
  val<THREE.Vector3>(u.limb).set(s.limb[0], s.limb[1], s.limb[2]);
  val<THREE.Vector3>(u.limbRed).set(s.limbRed[0], s.limbRed[1], s.limbRed[2]);
  val<THREE.Vector4>(u.gran).set(s.gran[0], s.gran[1], s.gran[2], s.gran[3]);
  val<THREE.Vector2>(u.granSpeed).set(s.granSpeed[0], s.granSpeed[1]);
  val<THREE.Vector4>(u.spot).set(s.spot[0], s.spot[1], s.spot[2], s.spot[3]);
  val<THREE.Vector3>(u.spotScale).set(s.spotScale[0], s.spotScale[1], s.spotScale[2]);
  // a sphere carries its own foreshortening; the flat field fakes it with a + bμ
  if (s.sphere) { val<THREE.Vector2>(u.fore).set(1, 0); u.sphereZ.value = 1; } else { val<THREE.Vector2>(u.fore).set(0.35, 0.65); u.sphereZ.value = 0; }
  val<THREE.Vector4>(u.rim).set(s.rim[0], s.rim[1], s.rim[2], s.rim[3]);
  // the rim and the soft edge must die inside the disc quad (DISC_EXT radii), or the quad's own square shows
  u.rimWidth.value = Math.max(1e-3, Math.min(0.06, s.rimWidth));
  u.fuzz.value = Math.max(1e-3, Math.min(0.05, s.fuzz));
  u.coronaPow.value = s.coronaPow;
  u.coronaRays.value = s.coronaRays;
  val<THREE.Vector4>(u.patchLook).set(s.patchRgb[0], s.patchRgb[1], s.patchRgb[2], Math.max(0.02, s.patchWidth));
}
/** Everything a kind or a pair may have set on a body, back to the Sun's: the surface, the turn, the outline, the patches, the occluder. */
export function resetBody(b: StarBody): void {
  setBodySurface(b, SUN_SURFACE);
  setBodySpin(b, null);
  setBodyShape(b, 0, 1, 1, 0);
  setBodyNose(b, 1, 0, 0, 2);
  setBodyPatch(b, 0, 0, 0, 1, 0); setBodyPatch(b, 1, 0, 0, 1, 0);
  setBodyOccluder(b, null);
  b.glareScale = 1;
}
export function setSunSurface(s: Readonly<SunSurface>): void { setBodySurface(sunRig(), s); }
/** The rig as the Commons Reach draws it (a star view calls this when it leaves). */
export function resetSunSurface(): void { if (rig) resetBody(rig); }

/** The turn of the sphere under the noise: the rows of the matrix taking a view-space surface normal (x right, y up, z to the eye) into the star's frame (y its spin axis). Null = none. */
export function setBodySpin(b: StarBody, rows: readonly [number, number, number, number, number, number, number, number, number] | null): void {
  const r = rows ?? [1, 0, 0, 0, 1, 0, 0, 0, 1];
  val<THREE.Vector3>(b.u.spin0).set(r[0], r[1], r[2]); val<THREE.Vector3>(b.u.spin1).set(r[3], r[4], r[5]); val<THREE.Vector3>(b.u.spin2).set(r[6], r[7], r[8]);
}
/** The outline: the spin axis on screen (a unit vector in the quad's right/up frame), the apparent polar ÷ equatorial radius (≤ 1), gravity darkening. */
export function setBodyShape(b: StarBody, axisX: number, axisY: number, polar: number, gravDark: number): void {
  const l = Math.hypot(axisX, axisY);
  const ok = l > 1e-6 && Number.isFinite(l);
  val<THREE.Vector4>(b.u.shape).set(ok ? axisX / l : 0, ok ? axisY / l : 1, 1 / Math.max(0.3, Math.min(1, Number.isFinite(polar) ? polar : 1)), Number.isFinite(gravDark) ? gravDark : 0);
}
/** The tear-drop: toward (x, y) on screen, reaching `amount` disc radii past the round outline, `sharp` the point's narrowness. */
export function setBodyNose(b: StarBody, x: number, y: number, amount: number, sharp = 2): void {
  const l = Math.hypot(x, y);
  const ok = l > 1e-6 && Number.isFinite(l);
  val<THREE.Vector4>(b.u.nose).set(ok ? x / l : 1, ok ? y / l : 0, ok && Number.isFinite(amount) ? Math.max(0, amount) : 0, Math.max(0.5, sharp));
}
/** Bright patch `i` (0 | 1) at a unit direction in the STAR's frame (it turns with the surface), `gain` × the disc's own emission. */
export function setBodyPatch(b: StarBody, i: 0 | 1, x: number, y: number, z: number, gain: number): void {
  const l = Math.hypot(x, y, z) || 1;
  val<THREE.Vector4>(i === 0 ? b.u.patchA : b.u.patchB).set(x / l, y / l, z / l, Number.isFinite(gain) ? Math.max(0, gain) : 0);
}
/** The nearer star of a pair, as this body's quad sees it: centre and radius in THIS body's disc radii (right / up), `soft` its edge, `k` how much of the overlap it takes. Null = none. */
export function setBodyOccluder(b: StarBody, o: { x: number; y: number; r: number; soft?: number; k?: number } | null): void {
  const fin = o && Number.isFinite(o.x) && Number.isFinite(o.y) && Number.isFinite(o.r) && o.r > 0;
  if (!o || !fin) { val<THREE.Vector4>(b.u.occ).set(1e4, 1e4, 0, 0.01); b.u.occK.value = 1; return; }
  val<THREE.Vector4>(b.u.occ).set(o.x, o.y, o.r, Math.max(1e-3, o.soft ?? 0.01));
  b.u.occK.value = Math.max(0, Math.min(1, o.k ?? 1));
}

// ── the star behind a black hole: what the lens controller writes (lib/space/black_hole_look.ts) ─────────────────
/** Every star body the sector is drawing now: the rig, and a pair's second star while it stands under the root. */
export function sunBodies(): StarBody[] {
  const out: StarBody[] = [];
  if (rig && rig.group.parent) out.push(rig);
  for (const b of bodies) if (b && b.group.parent) out.push(b);
  return out;
}
/**
 * Hand a body's disc and corona to the lens (true) or back to its quads (false). Written only on a CHANGE, so a bed or
 * a view that hides a quad for its own reasons is not overridden every frame. The glare stays a quad either way.
 */
export function setBodyLensOwned(b: StarBody, owned: boolean): void {
  if (b.lensOwned === owned) return;
  b.lensOwned = owned;
  b.disc.visible = !owned; b.corona.visible = !owned;
  if (!owned) { b.lens.on.value = 0; b.glare.position.set(0, 0, 0); }
}
const _e1 = new THREE.Vector3(), _e2 = new THREE.Vector3(), _s = new THREE.Vector3();
/** The lens source's uniforms for this frame (`seat` from black_hole.starLensSeat; `right` / `up` the camera's — the quads' frame). */
export function writeBodyLens(b: StarBody, seat: { front: boolean; sHat: { x: number; y: number; z: number }; kappa: number; rho: number }, right: THREE.Vector3, up: THREE.Vector3, on: boolean): void {
  const L = b.lens;
  const fin = Number.isFinite(seat.sHat.x) && Number.isFinite(seat.sHat.y) && Number.isFinite(seat.sHat.z) && Number.isFinite(seat.kappa) && seat.rho > 0 && Number.isFinite(seat.rho);
  val<THREE.Vector3>(L.sHat).set(fin ? seat.sHat.x : 0, fin ? seat.sHat.y : 1, fin ? seat.sHat.z : 0);
  L.kappa.value = fin ? Math.max(0, seat.kappa) : 0;
  L.rho.value = fin ? Math.min(0.999, seat.rho) : 0.01;
  // the star's own frame: the camera's right / up made perpendicular to the direction of its centre (Gram–Schmidt), so a
  // point of the disc keeps the bearing the quads give it while its distance from the centre stays the sphere's
  const sx = fin ? seat.sHat.x : 0, sy = fin ? seat.sHat.y : 1, sz = fin ? seat.sHat.z : 0;
  const e1 = _e1.copy(right).addScaledVector(_s.set(sx, sy, sz), -(right.x * sx + right.y * sy + right.z * sz));
  if (e1.lengthSq() < 1e-8) e1.copy(up).addScaledVector(_s, -(up.x * sx + up.y * sy + up.z * sz)); // the star dead along the camera's right: any perpendicular
  e1.normalize();
  const e2 = _e2.copy(up).addScaledVector(_s, -(up.x * sx + up.y * sy + up.z * sz)).addScaledVector(e1, -up.dot(e1));
  if (e2.lengthSq() < 1e-8) e2.crossVectors(_s, e1);
  e2.normalize();
  val<THREE.Vector3>(L.right).copy(e1); val<THREE.Vector3>(L.up).copy(e2);
  L.front.value = seat.front ? 1 : 0;
  L.on.value = on && fin ? 1 : 0;                                // a seat that is not a number draws NOTHING (never a NaN sky)
}
/**
 * Seat a lens-owned body's GLARE on the star's image: `imageDir` the unit direction the camera sees it in, `gain` 0…1
 * over what `placeBody` set (an image drawn into an arc has no point for a lens flare to stand on — the bloom carries it).
 */
export function seatBodyGlare(b: StarBody, imageDir: { x: number; y: number; z: number }, gain: number): void {
  const ok = Number.isFinite(imageDir.x) && Number.isFinite(imageDir.y) && Number.isFinite(imageDir.z);
  if (ok) b.glare.position.set((imageDir.x - b.dirNow.x) * b.quadD, (imageDir.y - b.dirNow.y) * b.quadD, (imageDir.z - b.dirNow.z) * b.quadD);
  b.u.glare.value = b.u.glare.value * (Number.isFinite(gain) ? Math.max(0, Math.min(1, gain)) : 0);
}
